// ─── Semantic Route Keys ─────────────────────────────────────────────────────
export enum RouteKeys {
  // Public
  HOME = "home",
  PRICING = "pricing",
  PRIVACY_POLICY = "privacy-policy",
  DATA_PRIVACY = "data-privacy",
  TERMS_OF_SERVICE = "terms-of-service",

  // Auth
  SIGN_IN = "sign-in",
  SIGN_UP = "sign-up",
  FORGOT_PASSWORD = "forgot-password",
  CONFIRM_EMAIL = "confirm-email",
  CONFIRM_NEW_EMAIL = "confirm-new-email",
  PASSWORD_CHANGE = "password-change",

  // App — customer portal
  DASHBOARD = "dashboard",
  SCANNER = "scanner",
  SCANNER_SEND = "scanner-send",
  SCANNER_PACKAGES = "scanner-packages",
  SCANNER_PACKAGE_DETAIL = "scanner-package-detail",
  CONTRACTS = "contracts",
  CONTRACT_DETAIL = "contract-detail",
  LOCATIONS = "locations",
  LOCATION_DETAIL = "location-detail",
  LOCATION_EDIT = "location-edit",
  EXTERNAL_DESTINATIONS = "external-destinations",
  EXTERNAL_DESTINATION_DETAIL = "external-destination-detail",
  EXTERNAL_DESTINATION_CREATE = "external-destination-create",
  EXTERNAL_DESTINATION_EDIT = "external-destination-edit",
  SHOPIFY = "shopify",
  PROFILE = "profile",
  PROFILE_EDIT = "profile-edit",
  BILLING = "billing",
  BILLING_SUCCESS = "billing-success",
  ANALYTICS = "analytics",
  DESTINATIONS = "destinations",
  DEFECT_REPORTING = "defect-reporting",
  USERS_MANAGEMENT = "users-management",
  REDISTRIBUTION_PLAN = "redistribution-plan",
  REORDER = "reorder",
  HELP = "help",

  // Admin — hey circle employee panel
  ADMIN_DASHBOARD = "admin-dashboard",
  ADMIN_USERS = "admin-users",
  ADMIN_USERS_CREATE = "admin-users-create",
  ADMIN_USERS_EDIT = "admin-users-edit",
  ADMIN_COMPANIES = "admin-companies",
  ADMIN_PACKAGES = "admin-packages",
  ADMIN_CONTRACTS = "admin-contracts",
  ADMIN_USERS_MANAGEMENT = "admin-users-management",
  ADMIN_PACKAGING_TYPES = "admin-packaging-types",
  ADMIN_MONITORING = "admin-monitoring",
}

// ─── Domain-Grouped Path Enums ───────────────────────────────────────────────

export enum PublicPaths {
  HOME = "/",
  PRICING = "/pricing",
  PRIVACY_POLICY = "/privacy-policy",
  DATA_PRIVACY = "/data-privacy",
  TERMS_OF_SERVICE = "/terms-of-service",
}

export enum AuthPaths {
  SIGN_IN = "/sign-in",
  SIGN_UP = "/sign-up",
  FORGOT_PASSWORD = "/forgot-password",
  CONFIRM_EMAIL = "/confirm-email",
  CONFIRM_NEW_EMAIL = "/confirm-new-email",
  PASSWORD_CHANGE = "/password-change",
}

export enum AppPaths {
  DASHBOARD = "/app/dashboard",
  SCANNER = "/app/scanner",
  SCANNER_SEND = "/app/scanner/send",
  SCANNER_PACKAGES = "/app/scanner/packages",
  CONTRACTS = "/app/contracts",
  LOCATIONS = "/app/destinations/locations",
  EXTERNAL_DESTINATIONS = "/app/destinations/external",
  EXTERNAL_DESTINATION_CREATE = "/app/destinations/external/new",
  SHOPIFY = "/app/shopify",
  PROFILE = "/app/profile",
  PROFILE_EDIT = "/app/profile/edit",
  BILLING = "/app/billing",
  BILLING_SUCCESS = "/app/billing/success",
  ANALYTICS = "/app/analytics",
  DESTINATIONS = "/app/destinations",
  DEFECT_REPORTING = "/app/support/defect-reporting",
  USERS_MANAGEMENT = "/app/users",
  REDISTRIBUTION_PLAN = "/app/inventory/redistribute",
  REORDER = "/app/inventory/reorder",
  HELP = "/app/help",
}

export enum AdminPaths {
  DASHBOARD = "/admin/dashboard",
  USERS = "/admin/users",
  USERS_CREATE = "/admin/users/create",
  COMPANIES = "/admin/companies",
  PACKAGES = "/admin/packages",
  CONTRACTS = "/admin/contracts",
  USERS_MANAGEMENT = "/admin/users-management",
  PACKAGING_TYPES = "/admin/packaging-types",
  MONITORING = "/admin/monitoring",
}

// ─── Types ───────────────────────────────────────────────────────────────────

type Route = { href: string };

/** Keys that require parameters — excluded from the static ROUTES map */
export type DynamicRouteKey =
  | RouteKeys.SCANNER_PACKAGE_DETAIL
  | RouteKeys.CONTRACT_DETAIL
  | RouteKeys.LOCATION_DETAIL
  | RouteKeys.LOCATION_EDIT
  | RouteKeys.EXTERNAL_DESTINATION_DETAIL
  | RouteKeys.EXTERNAL_DESTINATION_EDIT
  | RouteKeys.ADMIN_USERS_EDIT;

type StaticRouteKey = Exclude<RouteKeys, DynamicRouteKey>;

// ─── Static Route Registry (exhaustive — omitting a key is a compile error) ─

export const ROUTES: { [K in StaticRouteKey]: Route } = {
  // Public
  [RouteKeys.HOME]: { href: PublicPaths.HOME },
  [RouteKeys.PRICING]: { href: PublicPaths.PRICING },
  [RouteKeys.PRIVACY_POLICY]: { href: PublicPaths.PRIVACY_POLICY },
  [RouteKeys.DATA_PRIVACY]: { href: PublicPaths.DATA_PRIVACY },
  [RouteKeys.TERMS_OF_SERVICE]: { href: PublicPaths.TERMS_OF_SERVICE },

  // Auth
  [RouteKeys.SIGN_IN]: { href: AuthPaths.SIGN_IN },
  [RouteKeys.SIGN_UP]: { href: AuthPaths.SIGN_UP },
  [RouteKeys.FORGOT_PASSWORD]: { href: AuthPaths.FORGOT_PASSWORD },
  [RouteKeys.CONFIRM_EMAIL]: { href: AuthPaths.CONFIRM_EMAIL },
  [RouteKeys.CONFIRM_NEW_EMAIL]: { href: AuthPaths.CONFIRM_NEW_EMAIL },
  [RouteKeys.PASSWORD_CHANGE]: { href: AuthPaths.PASSWORD_CHANGE },

  // App
  [RouteKeys.DASHBOARD]: { href: AppPaths.DASHBOARD },
  [RouteKeys.SCANNER]: { href: AppPaths.SCANNER },
  [RouteKeys.SCANNER_SEND]: { href: AppPaths.SCANNER_SEND },
  [RouteKeys.SCANNER_PACKAGES]: { href: AppPaths.SCANNER_PACKAGES },
  [RouteKeys.CONTRACTS]: { href: AppPaths.CONTRACTS },
  [RouteKeys.LOCATIONS]: { href: AppPaths.LOCATIONS },
  [RouteKeys.EXTERNAL_DESTINATIONS]: {
    href: AppPaths.EXTERNAL_DESTINATIONS,
  },
  [RouteKeys.EXTERNAL_DESTINATION_CREATE]: {
    href: AppPaths.EXTERNAL_DESTINATION_CREATE,
  },
  [RouteKeys.SHOPIFY]: { href: AppPaths.SHOPIFY },
  [RouteKeys.PROFILE]: { href: AppPaths.PROFILE },
  [RouteKeys.PROFILE_EDIT]: { href: AppPaths.PROFILE_EDIT },
  [RouteKeys.BILLING]: { href: AppPaths.BILLING },
  [RouteKeys.BILLING_SUCCESS]: { href: AppPaths.BILLING_SUCCESS },
  [RouteKeys.ANALYTICS]: { href: AppPaths.ANALYTICS },
  [RouteKeys.DESTINATIONS]: { href: AppPaths.DESTINATIONS },
  [RouteKeys.DEFECT_REPORTING]: { href: AppPaths.DEFECT_REPORTING },
  [RouteKeys.USERS_MANAGEMENT]: { href: AppPaths.USERS_MANAGEMENT },
  [RouteKeys.REDISTRIBUTION_PLAN]: { href: AppPaths.REDISTRIBUTION_PLAN },
  [RouteKeys.REORDER]: { href: AppPaths.REORDER },
  [RouteKeys.HELP]: { href: AppPaths.HELP },

  // Admin
  [RouteKeys.ADMIN_DASHBOARD]: { href: AdminPaths.DASHBOARD },
  [RouteKeys.ADMIN_USERS]: { href: AdminPaths.USERS },
  [RouteKeys.ADMIN_USERS_CREATE]: { href: AdminPaths.USERS_CREATE },
  [RouteKeys.ADMIN_COMPANIES]: { href: AdminPaths.COMPANIES },
  [RouteKeys.ADMIN_PACKAGES]: { href: AdminPaths.PACKAGES },
  [RouteKeys.ADMIN_CONTRACTS]: { href: AdminPaths.CONTRACTS },
  [RouteKeys.ADMIN_USERS_MANAGEMENT]: { href: AdminPaths.USERS_MANAGEMENT },
  [RouteKeys.ADMIN_PACKAGING_TYPES]: { href: AdminPaths.PACKAGING_TYPES },
  [RouteKeys.ADMIN_MONITORING]: { href: AdminPaths.MONITORING },
};

// ─── Dynamic Route Builders ──────────────────────────────────────────────────

export const DYNAMIC_ROUTES = {
  [RouteKeys.SCANNER_PACKAGE_DETAIL]: (packageId: string) => `/app/scanner/packages/${packageId}`,
  [RouteKeys.CONTRACT_DETAIL]: (id: string) => `/app/contracts/${id}`,
  [RouteKeys.LOCATION_DETAIL]: (id: string) => `/app/destinations/locations/${id}`,
  [RouteKeys.LOCATION_EDIT]: (id: string) => `/app/destinations/locations/${id}/edit`,
  [RouteKeys.EXTERNAL_DESTINATION_DETAIL]: (id: string) => `/app/destinations/external/${id}`,
  [RouteKeys.EXTERNAL_DESTINATION_EDIT]: (id: string) => `/app/destinations/external/${id}/edit`,
  [RouteKeys.ADMIN_USERS_EDIT]: (id: string) => `/admin/users/edit/${id}`,
} as const;

// ─── Convenience Helpers ─────────────────────────────────────────────────────

/** Get a static route path by key */
export function route(key: StaticRouteKey): string {
  return ROUTES[key].href;
}

/** Build a dynamic route path with parameters */
export function dynamicRoute<K extends DynamicRouteKey>(
  key: K,
  ...args: Parameters<(typeof DYNAMIC_ROUTES)[K]>
): string {
  return (DYNAMIC_ROUTES[key] as (...a: string[]) => string)(...args);
}
